/**
 * @module app.um
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ContactLogService } from 'app/core/services/contact-log-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { Toaster } from 'app/shared/lib/toaster';
import { EventAggregator } from 'aurelia-event-aggregator';
import { ContactLogOutcomeReloadEvent } from 'app/um/lib/events';
import { IContactLogConfig } from 'app/core/models/essette-interfaces';
import { forceNull } from 'app/shared/lib/utilities';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class SaveMethod extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay, changeHandler: 'reload' })
    public method: UM.AuthContactLogMethod = {};

    @bindable
    public config: IContactLogConfig = {};

    protected contactMethodText: string = '';
    protected contactMethodCodeText: string = '';
    protected initiateEmail: boolean = false;

    protected originalMethod: UM.AuthContactLogMethod = {};

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly service: ContactLogService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        if(!this.config) { throw new Error('Bindable property [config] was not set'); }

        this.service.configure(this.config);
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    private async reload(): Promise<void> {
        if(!this.method) return;

        // Params for method info
        this.contactMethodText = this.method.description || '';
        this.contactMethodCodeText = this.method.code || '';
        this.initiateEmail = this.method.flag?.sendsEmail || false;
        this.originalMethod = this.buildMethod();
    }

    protected buildMethod(): UM.AuthContactLogMethod {
        const saveMethod: UM.AuthContactLogMethod = {
            id: this.method.id,
            description: this.contactMethodText,
            code: forceNull(this.contactMethodCodeText),
            flag: { sendsEmail: this.initiateEmail }
        };

        return saveMethod;
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        const isSuccess = await this.saveMethod();
        if(isSuccess) this.events.publish(new ContactLogOutcomeReloadEvent());
        return isSuccess;
    }

    public reset(): void {
        this.initiateEmail = this.originalMethod.flag?.sendsEmail || false;
        this.contactMethodText = this.originalMethod.description || '';
        this.contactMethodCodeText = this.originalMethod.code || '';
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async saveMethod(): Promise<boolean> {
        const insertMethod: UM.AuthContactLogMethod = this.buildMethod();

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.saveContactLogMethod(insertMethod);
            isSuccessful = response.isSuccessful;

            return isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.saved('core:contactLog.method.$long', isSuccessful);
            this.state.endSave();
        }
    }
}
